import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import SaveIcon from '@mui/icons-material/Save';
import { SubmitEvent, useCallback, useState } from 'react';
import { FormLabel, IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { red } from '../../theme/themePrimitives';
import ConsumableLineItemForm, { ConsumableLineItemFormState } from './ConsumableLineItemForm';
import { type ConsumableLineItem, ConsumableLineItemUseType, validate as validateConsumableLineItem } from '../../models/ConsumableLineItem';

export interface ConsumableFormProps {
  initialItems?: ConsumableLineItem[];
  onSubmit: (consumableLineItems: ConsumableLineItem[]) => Promise<void>;
}

export default function ConsumableForm(props: ConsumableFormProps) {
  const {
    initialItems = [],
    onSubmit
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consumableLineItems, setConsumableLineItems] = useState<ConsumableLineItem[]>(initialItems);
  const [consumableLineItemsErrors, setConsumableLineItemsErrors] = useState<Map<number, Partial<Record<keyof ConsumableLineItemFormState['values'], string>>>>(new Map());

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);

      const lineItemErrors = new Map<number, Partial<Record<keyof ConsumableLineItemFormState['values'], string>>>();

      if ((consumableLineItems || []).length > 0) {
        for (let index = 0; index < consumableLineItems.length; index++) {
          const consumableLineItem = consumableLineItems[index];

          const { issues } = validateConsumableLineItem(consumableLineItem);

          let errors = null;

          if (issues && issues.length > 0) {
            errors = Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message]));
          }

          if (errors) {
            lineItemErrors.set(index, errors);
          }
        }
      }

      setConsumableLineItemsErrors(
        lineItemErrors);

      if (lineItemErrors.size > 0) {
        setIsSubmitting(false);
        return;
      }

      try {
        await onSubmit(consumableLineItems);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, consumableLineItems],
  );

  const handleConsumableLineItemFormFieldChange = useCallback(
    (updatedIngredient: Partial<ConsumableLineItemFormState['values']>, index: number) => {
      const updatedConsumableLineItems = [...consumableLineItems];

      updatedConsumableLineItems[index] = { ...consumableLineItems[index], ...updatedIngredient };

      setConsumableLineItems(updatedConsumableLineItems);
    },
    [consumableLineItems],
  );

  const addNewConsumableLineItem = useCallback(() => {
    setConsumableLineItems(
      [...consumableLineItems, {
        id: 0,
        description: '',
        consumableLineItemUseType: ConsumableLineItemUseType.YEARLY,
        orderQuantity: 0,
        unitQuantity: 1,
        unitCost: 0.00
      }]);
  }, [consumableLineItems]);

  const removeConsumableLineItem = useCallback((indexToRemove: number) => {
    const updatedConsumableLineItems = consumableLineItems.filter((_, index) => index !== indexToRemove);
    setConsumableLineItems(updatedConsumableLineItems);
  }, [consumableLineItems]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid spacing={0} sx={{ mb: 2, width: '100%' }}>
          <Stack spacing={2} sx={{ width: '100%' }}>
            <FormLabel>
              Consumable Line Items
            </FormLabel>
            <div>
              <Button
                type="button"
                color="secondary"
                startIcon={<Add />}
                variant="outlined"
                onClick={addNewConsumableLineItem}>
                Add Consumable Line Item
              </Button>
            </div>
          </Stack>
        </Grid>
        <Grid container spacing={1} sx={{ mb: 2, pt: 2, width: '100%' }}>
          <Grid spacing={0} sx={{ width: '90%' }}>
            {consumableLineItems?.map((consumableLineItem: ConsumableLineItem, index: number) => (
              <Stack key={index} direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
                <ConsumableLineItemForm
                  initialValue={consumableLineItem}
                  initialErrors={consumableLineItemsErrors.get(index)}
                  onFormValuesUpdate={(itemFormValues) => handleConsumableLineItemFormFieldChange(itemFormValues, index)} />
                <Box sx={{ width: '36px' }}>
                  <IconButton
                    size="small"
                    style={{ marginTop: '8px' }}
                    onClick={() => removeConsumableLineItem(index)}>
                    <Delete htmlColor={red[400]} />
                  </IconButton>
                </Box>
              </Stack>
            ))}
          </Grid>
        </Grid>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Box></Box>
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="success"
          startIcon={<SaveIcon />}
          loading={isSubmitting}
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
}
